import {Observable, of} from "rxjs";
import {finalize, map, mergeMap, tap} from "rxjs/operators";
import {ChangeDetectorRef, Component, HostListener, Input, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {ConfirmationComponent} from "@shared/components/confirmation/confirmation.component";
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {TicketUtils} from "@app/layout/sites/tickets/ticket-utils";
import {TicketOperation, TicketOperationResult, TicketUpsertData, TicketUpsertForm} from '@app/layout/sites/tickets/tickets.interface';
import {EscalatedTicket, EscalatedTicketBody, TicketAPIResultFull, TicketReferenceData, TicketsService, resolvedTicketStatusIds} from '@shared/services';
import {defaultTicketModalOptions, TicketModal, TicketModalUtil} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';
import {TicketCommentTrackingEvent, TicketTrackingEvent, TicketTrackingService} from "@app/layout/sites/tickets/ticket-tracking.service";
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {UserProfileService} from "@app/shared/services/api/user-profile.service";
import {EmailType} from "../email-ticket/email-ticket.component";

enum InProgressOperation {
  SAVING,
  ARCHIVING,
  DELETING
}

export enum EscalatedTicketUpsertFormat {
  SIMPLE = 'simple',
  NORMAL = 'normal'
}

export interface EscalatedTicketUpsertModal extends TicketModal<EscalatedTicket> {
  format?: EscalatedTicketUpsertFormat;
}

@Component({
  selector: 'app-escalated-ticket-upsert-modal',
  templateUrl: './escalated-ticket-upsert-modal.component.html',
  styleUrls: ['./escalated-ticket-upsert-modal.component.scss'],
  standalone: false
})
export class EscalatedTicketUpsertModalComponent implements EscalatedTicketUpsertModal {

  @Input()
  site: SiteInfo | null;

  @Input()
  ticket: EscalatedTicket | Partial<EscalatedTicket>;

  @Input()
  format?: EscalatedTicketUpsertFormat = EscalatedTicketUpsertFormat.NORMAL;

  @ViewChild('ticketUpsertForm')
  ticketUpsertForm?: TicketUpsertForm;

  EscalatedTicketUpsertFormat = EscalatedTicketUpsertFormat;
  InProgressOperation = InProgressOperation;
  executingOperation: InProgressOperation = null;
  ticketUpsertData: TicketUpsertData = null;
  loading = false;
  pending = false;

  constructor(private cdRef: ChangeDetectorRef, private modalService: NgbModal, public activeModal: NgbActiveModal,
              private ticketsService: TicketsService, private ticketDataCaches: TicketDataCaches,
              private ticketTrackingService: TicketTrackingService, private userProfileService: UserProfileService) {
  }

  static openInModal(modalService: NgbModal, data: EscalatedTicketUpsertModal, modalOptions?: NgbModalOptions): NgbModalRef {
    const modalRef = modalService.open(EscalatedTicketUpsertModalComponent, {
      ...defaultTicketModalOptions,
      ...data.format === EscalatedTicketUpsertFormat.SIMPLE && {windowClass: 'modal-md'},
      ...modalOptions,
    });
    const modalInstance: EscalatedTicketUpsertModal = modalRef.componentInstance;
    TicketModalUtil.inflateModalInstance(modalInstance, data);
    if (data.format) {
      modalInstance.format = data.format;
    }
    return modalRef;
  }

  closeModal() {
    this.activeModal.close();
  }

  @HostListener('window:keyup.escape', ['$event'])
  keyEvent(_event: KeyboardEvent) {
    // We disable keyboard tracking for this modal as when editing a ticket this is a nested modal (above the Ticket View Modal),
    // and we want to ensure that the ESC key only closes this (outer) modal and not the Ticket View Modal. To do that we need to
    // close this modal manually, as otherwise the modal event is sent before the key event is received by the View Ticket Modal
    // and the View Ticket modal is not able to determine if another modal (i.e. this one) is open
    this.activeModal.dismiss('escape key pressed');
  }

  asFullTicket(ticket: EscalatedTicket | Partial<EscalatedTicket>): EscalatedTicket {
    return <EscalatedTicket>ticket;
  }

  onTicketUpsertData(ticketUpsertData: TicketUpsertData) {
    this.ticketUpsertData = ticketUpsertData;
    this.cdRef.detectChanges();
  }

  onLoading(loading: boolean) {
    if (this.loading !== loading) {
      this.loading = loading;
      this.cdRef.detectChanges();
    }
  }

  onPending(pending: boolean) {
    this.pending = pending;
  }

  cancelSaveTicket(): void {
    this.activeModal.dismiss('Cancel save');
  }

  saveTicket(): void {
    if (this.ticketUpsertData) {
      TicketUtils.linkActionToAlerts(this.ticket, this.ticketUpsertData.linkedAlerts, this.modalService, {
        onConfirmUpdateLink: this.doSaveTicket.bind(this)
      });
    } else {
      this.ticketUpsertForm.showErrors();
    }
  }

  toggleCurrentActionArchived() {
    if (this.ticket?.ticket_id && this.ticket.ticket_archived) {
      this.setActionArchived(false).subscribe(result => this.handleSetActionArchivedResult(false, result));
    } else {
      this.confirmArchiveAction();
    }
  }

  confirmDeleteAction(): void {
    ConfirmationComponent.createModal(this.modalService, {
      title: {messageKey: 'Delete Action'},
      body: {
        messageKey: 'Confirm delete action with name?',
        parameters: {actionName: this.ticket.summary}
      },
      confirmFn: () => {
        this.executingOperation = InProgressOperation.DELETING;
        return this.ticketsService.deleteTicket(this.ticket.ticket_id)
          .pipe(finalize(() => this.executingOperation = null));
      },
      afterConfirmFn: () => {
        this.ticketTrackingService.trackTicket(TicketTrackingEvent.DELETED, this.asFullTicket(this.ticket));
        this.onTicketOperation({operation: TicketOperation.DELETE, ticket: this.ticket});
      }
    });
  }

  private onTicketOperation(result: TicketOperationResult<EscalatedTicket>) {
    this.activeModal.close(result);
  }

  private doSaveTicket() {
    this.executingOperation = InProgressOperation.SAVING;
    const create = !this.ticket?.ticket_id;
    this.upsertTicket(create).pipe(
      mergeMap(ticketOperationResult => this.addComment(ticketOperationResult)),
      mergeMap(ticketOperationResult => this.sendAssignEmail(ticketOperationResult)),
      finalize(() => this.executingOperation = null)
    ).subscribe(result => this.onTicketOperation(result));
  }

  private setActionArchived(archived: boolean): Observable<TicketAPIResultFull<EscalatedTicket>> {
    this.executingOperation = InProgressOperation.ARCHIVING;
    return this.ticketsService.setTicketArchived(this.asFullTicket(this.ticket), archived, <TicketReferenceData>{})
      .pipe(finalize(() => this.executingOperation = null));
  }

  private upsertTicket(create: boolean): Observable<TicketOperationResult<EscalatedTicket>> {
    const escalatedTicketsById = CollectionUtils.indexBy(this.ticketUpsertData.linkedAlerts?.map(TicketUtils.getAlertLinkedAction).filter(Boolean), 'ticket_id');
    const oldTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());

    return this.getTicketReferenceData().pipe(
      mergeMap(ticketReferenceData => {
        let ticketUpsert$: Observable<TicketAPIResultFull<EscalatedTicket>>;
        if (create) {
          ticketUpsert$ = this.ticketsService.createEscalatedTicket(this.ticketUpsertData.ticketBody, ticketReferenceData);
        } else {
          const t = this.asFullTicket(this.ticket);
          escalatedTicketsById.set(this.ticket.ticket_id, t);
          ticketUpsert$ = this.ticketsService.updateEscalatedTicket(t, <EscalatedTicketBody>this.ticketUpsertData.ticketBody, ticketReferenceData);
        }
        return ticketUpsert$;
      }),
      map((result: TicketAPIResultFull<EscalatedTicket>) => {
        escalatedTicketsById.set(result.ticket.ticket_id, result.ticket);
        const newTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());
        this.ticketDataCaches.updateRuleTicketCache(oldTaskIdsByTicketId, newTaskIdsByTicketId, escalatedTicketsById);
        this.ticketTrackingService.trackTicket(create ? TicketTrackingEvent.CREATED : TicketTrackingEvent.UPDATED, result.ticket);
        if (create && resolvedTicketStatusIds.has(result.ticket.status_id)) {
          this.ticketTrackingService.trackTicket(TicketTrackingEvent.STATUS_MOVED, result.ticket, {status_from: null, status_to: result.ticket.status.display_text});
        }
        return({
          operation: create ? TicketOperation.CREATE : TicketOperation.UPDATE,
          ticket: result.ticket,
          affectedTickets: result.affected_tickets,
        });
      }));
  }

  private addComment(ticketOperationResult: TicketOperationResult<EscalatedTicket>): Observable<TicketOperationResult<EscalatedTicket>> {
    if (this.ticketUpsertData.comment) {
      return this.ticketsService.createTicketComment({text: this.ticketUpsertData.comment, ticket_id: ticketOperationResult.ticket.ticket_id}, true).pipe(
        tap(comment => this.ticketTrackingService.trackComment(
          TicketCommentTrackingEvent.CREATED,
          <EscalatedTicket>ticketOperationResult.ticket,
          comment.ticket_comment)
        ),
        map(comment => {
          ticketOperationResult.ticket.comments = [comment.ticket_comment];
          Object.assign(ticketOperationResult.ticket, comment.ticket);
          ticketOperationResult.ticket.comment_count = 1;
          return ticketOperationResult;
        })
      );
    } else {
      return of(ticketOperationResult);
    }
  }

  private sendAssignEmail(ticketOperationResult: TicketOperationResult<EscalatedTicket>): Observable<TicketOperationResult<EscalatedTicket>> {
    if (!ticketOperationResult.ticket.comments?.length
        && !resolvedTicketStatusIds.has(ticketOperationResult.ticket.status_id)
        && this.format === EscalatedTicketUpsertFormat.SIMPLE) {
      const user = this.userProfileService.getLoggedInUser();
      const assignees = ticketOperationResult.ticket.assignee_ids
        .filter(assigneeId => assigneeId !== user?.userId)
        .map(assigneeId => this.ticketUpsertData.assigneesById?.get(assigneeId))
        .filter(Boolean);
      if (assignees.length) {
        const ticket = <EscalatedTicket>ticketOperationResult.ticket;
        const email = this.ticketsService.prepareSimpleAssignEmail(ticket, user.asUser(), assignees);
        return this.ticketsService.sendEmail(email, ticketOperationResult.ticket.ticket_id).pipe(
          map(() => ticketOperationResult),
          finalize(() => this.ticketTrackingService.trackTicket(TicketTrackingEvent.EMAILED, ticket, {type: EmailType.ASSIGN}))
        );
      }
    }
    return of(ticketOperationResult);
  }

  private confirmArchiveAction(): void {
    ConfirmationComponent.createModal(this.modalService, {
      title: { messageKey: 'Archive Action' },
      body: {
        messageKey: 'Confirm archive action with name?',
        parameters: { actionName: this.ticket.summary }
      },
      confirmFn: () => {
        const escalatedTicketsById = CollectionUtils.indexBy([this.asFullTicket(this.ticket)], 'ticket_id');
        const oldTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());
        return this.setActionArchived(true).pipe(tap(() => {
          const newTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());
          this.ticketDataCaches.updateRuleTicketCache(oldTaskIdsByTicketId, newTaskIdsByTicketId, escalatedTicketsById);
        }));
      },
      afterConfirmFn: (result: TicketAPIResultFull<EscalatedTicket>) => this.handleSetActionArchivedResult(true, result)
    });
  }

  private handleSetActionArchivedResult(archived: boolean, result: TicketAPIResultFull<EscalatedTicket>): void {
    this.ticketTrackingService.trackTicket(archived ? TicketTrackingEvent.ARCHIVED : TicketTrackingEvent.UNARCHIVED, this.asFullTicket(this.ticket));
    this.onTicketOperation(TicketUtils.convertAPIResultToOperationResult(TicketOperation.UPDATE, result));
  }

  private getTicketReferenceData(): Observable<TicketReferenceData> {
    return this.ticketDataCaches.getPrioritiesById().pipe(map(prioritiesById => ({
      site: this.ticketUpsertData.site,
      assigneesById: this.ticketUpsertData.assigneesById,
      prioritiesById
    })));
  }
}
